import {
  allowsOptionalStorage,
  COOKIE_CONSENT,
  COOKIE_CONSENT_EVENT,
  getCookieConsent,
} from "@/lib/cookieConsent";

export const DISMISS_KEY = "lapstr:pwa-install-dismissed-at";
export const INSTALLED_KEY = "lapstr:pwa-installed-at";
export const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
const INSTALLED_COOKIE = "lapstr_pwa_installed";

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (typeof navigator !== "undefined" && "standalone" in navigator && navigator.standalone)
  );
}

export function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return isIos() && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

export function isIosChrome() {
  if (typeof navigator === "undefined") return false;
  return isIos() && /crios/i.test(navigator.userAgent);
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent);
}

export function isMobileInstallSupported() {
  return isIos() || isAndroid();
}

export function readTs(key) {
  try {
    const value = window.localStorage.getItem(key);
    return value ? Number(value) : 0;
  } catch {
    return 0;
  }
}

export function writeTs(key) {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {}
}

function hasInstalledCookie() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((part) => part.trim().startsWith(`${INSTALLED_COOKIE}=`));
}

function writeInstalledCookie() {
  if (typeof document === "undefined") return;
  if (!allowsOptionalStorage()) return;
  document.cookie = `${INSTALLED_COOKIE}=1; path=/; max-age=31536000; samesite=lax`;
}

export function clearInstalledCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${INSTALLED_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export function hasInstallMarker() {
  if (typeof window === "undefined") return false;
  return Boolean(readTs(INSTALLED_KEY)) || hasInstalledCookie();
}

export function markInstalled() {
  writeTs(INSTALLED_KEY);
  writeInstalledCookie();
}

export function getInstallPlatform() {
  if (isIosSafari()) return "ios_safari";
  if (isIosChrome()) return "ios_chrome";
  if (isIos()) return "ios";
  if (isAndroid()) return "android";
  return null;
}

export function shouldShowManualInstallGuide() {
  return Boolean(getInstallPlatform()) && !isStandalone();
}

export function shouldAutoPromptInstall() {
  return Boolean(getInstallPlatform()) && !isStandalone() && !shouldRespectCooldown();
}

export function shouldRespectCooldown() {
  const lastDismissedAt = readTs(DISMISS_KEY);
  return Date.now() - lastDismissedAt < DISMISS_COOLDOWN_MS;
}

if (typeof window !== "undefined") {
  window.addEventListener(COOKIE_CONSENT_EVENT, () => {
    if (getCookieConsent() === COOKIE_CONSENT.NECESSARY) {
      clearInstalledCookie();
    }
  });
}
